import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

import { Input, Button, IconButton, Tooltip } from "../../design-system";
import ProjectImageField from "./ProjectImageField";

/**
 * Répéteur de captures d'un projet : image (URL ou upload), légende, alt.
 *
 * L'ordre du tableau est l'ordre d'affichage sur la page publique, d'où les
 * flèches de réordonnancement (pas de drag-and-drop ici : la galerie reste
 * courte, quelques images tout au plus).
 */
export default function ProjectGalleryInput({ value = [], onChange, slug }) {
  const items = Array.isArray(value) ? value : [];

  const update = (index, field, fieldValue) =>
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: fieldValue } : item)));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-4 py-6 text-center">
          <p className="text-sm text-mute italic">
            Aucune capture. Les images apparaîtront en galerie sur la page du projet.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((item, index) => (
            <li key={index} className="p-3 rounded-md bg-bg-2/40 border border-border space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10.5px] font-mono uppercase tracking-wider text-mute">
                  Capture {index + 1}
                </span>
                <div className="flex items-center gap-0.5">
                  <Tooltip content="Monter">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<ArrowUp strokeWidth={1.8} />}
                      aria-label={`Monter la capture ${index + 1}`}
                      isDisabled={index === 0}
                      onClick={() => move(index, -1)}
                    />
                  </Tooltip>
                  <Tooltip content="Descendre">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<ArrowDown strokeWidth={1.8} />}
                      aria-label={`Descendre la capture ${index + 1}`}
                      isDisabled={index === items.length - 1}
                      onClick={() => move(index, 1)}
                    />
                  </Tooltip>
                  <Tooltip content="Supprimer">
                    <IconButton
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 strokeWidth={1.8} />}
                      aria-label={`Supprimer la capture ${index + 1}`}
                      onClick={() => onChange(items.filter((_, i) => i !== index))}
                    />
                  </Tooltip>
                </div>
              </div>

              <ProjectImageField
                value={item.url}
                onChange={(url) => update(index, "url", url)}
                slug={slug}
                label={`Capture ${index + 1}`}
                compact
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input
                  value={item.caption || ""}
                  onChange={(e) => update(index, "caption", e.target.value)}
                  placeholder="Légende (affichée sous l'image)"
                  aria-label={`Légende de la capture ${index + 1}`}
                />
                <Input
                  value={item.alt || ""}
                  onChange={(e) => update(index, "alt", e.target.value)}
                  placeholder="Texte alternatif (accessibilité)"
                  aria-label={`Texte alternatif de la capture ${index + 1}`}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        size="sm"
        variant="secondary"
        leftIcon={<Plus className="w-4 h-4" strokeWidth={1.8} />}
        onClick={() => onChange([...items, { url: "", caption: "", alt: "" }])}
      >
        Ajouter une capture
      </Button>
    </div>
  );
}
